import { useState, useEffect } from 'react';

// ── Types ────────────────────────────────────────────────────────────────────

export interface PublicPaymentOrg {
  name: string;
  logo?: string;
  phone?: string;
  email?: string;
  brandPrimaryColor?: string;
  brandTitleTextColor?: string;
}

export interface PublicPaymentCustomer {
  name: string;
  email?: string;
  phone?: string;
  companyName?: string;
}

export interface PublicPaymentJob {
  originAddress: string;
  destinationAddress: string;
  moveDate: string;
  status: string;
}

export interface PublicPaymentScheduleItem {
  label: string;
  amountCents: number;
  remainingCents: number;
  status: 'pending' | 'due' | 'paid' | 'overdue' | 'waived';
}

export interface PublicPaymentInfo {
  totalAmountCents: number;
  collectedAmountCents: number;
  outstandingAmountCents: number;
  dueNowAmountCents: number;
  paymentStatus: string;
  schedule: PublicPaymentScheduleItem[];
  /** Tax breakdown (present only when tax was applied). */
  subtotalAmountCents?: number;
  taxState?: string;
  laborTaxRate?: number;
  taxAmountCents?: number;
  requiresManualTaxReview?: boolean;
}

export type PublicPaymentMethodType =
  | 'venmo'
  | 'cashapp'
  | 'paypal'
  | 'zelle'
  | 'check'
  | 'cash'
  | 'custom'
  | 'stripe';

export interface PublicPaymentMethod {
  type: PublicPaymentMethodType;
  label: string;
  deepLink?: string;
  identifier?: string;
  stripePaymentUrl?: string;
}

export interface PublicPaymentData {
  org: PublicPaymentOrg;
  customer: PublicPaymentCustomer;
  job: PublicPaymentJob;
  payment: PublicPaymentInfo;
  paymentMethods: PublicPaymentMethod[];
}

interface PublicPaymentResponse {
  success: boolean;
  data: PublicPaymentData;
  error?: string;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

export function usePublicPayment(orgId: string | undefined, quoteId: string | undefined) {
  const [data, setData] = useState<PublicPaymentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId || !quoteId) {
      setLoading(false);
      setError('Invalid link');
      return;
    }

    let cancelled = false;

    async function fetchData() {
      setLoading(true);
      setError(null);

      try {
        const res = await fetch(
          `${API_BASE}/api/public/payment/${encodeURIComponent(orgId!)}/${encodeURIComponent(quoteId!)}`,
        );

        if (!res.ok) {
          if (res.status === 404) {
            if (!cancelled) setError('not_found');
          } else {
            if (!cancelled) setError('Something went wrong. Please try again later.');
          }
          return;
        }

        const json = (await res.json()) as PublicPaymentResponse;

        if (!cancelled) {
          if (json.success) {
            setData(json.data);
          } else {
            setError(json.error ?? 'Failed to load payment data');
          }
        }
      } catch {
        if (!cancelled) setError('Unable to connect. Please check your internet connection.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchData();
    return () => { cancelled = true; };
  }, [orgId, quoteId]);

  return { data, loading, error };
}
